import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const STX = 0x02
const ENQ = 0x05
const BLOCK_SIZE = 8
const MEMORY_SIZE = 0x03f0
const TONE_DCS_N = 8000
const TONE_DCS_R = 12000

type R2Spec = {
  id: string
  vendor: 'Radioddity' | 'Retevis'
  model: string
  summary: string
  channels: 6 | 16
}

export type R2Profile = {
  protocol: 'r2'
  id: string
  vendor: 'Radioddity' | 'Retevis'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  memorySize: number
  powerLevels: readonly ['Low', 'High']
}

const SPECS: readonly R2Spec[] = [
  { id: 'radioddity-r2', vendor: 'Radioddity', model: 'R2', summary: 'Radioddity R2 compact UHF radio.', channels: 16 },
  { id: 'retevis-rt24', vendor: 'Retevis', model: 'RT24', summary: 'Retevis RT24 compact UHF radio.', channels: 16 },
  { id: 'retevis-rt24v', vendor: 'Retevis', model: 'RT24V', summary: 'Retevis RT24V compact VHF radio.', channels: 6 },
  { id: 'retevis-h777s', vendor: 'Retevis', model: 'H777S', summary: 'Retevis H777S compact UHF radio.', channels: 16 },
]

export const R2_PROFILES: readonly R2Profile[] = SPECS.map((spec) => ({
  protocol: 'r2',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', `${spec.channels} channels`, 'No channel names'],
  baudRate: 9600,
  channelMinimum: 1,
  channelMaximum: spec.channels,
  nameLength: 0,
  memorySize: MEMORY_SIZE,
  powerLevels: ['Low', 'High'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
  }
}

function rawBcdValue(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    value = value * 100 + (raw[index] >> 4) * 10 + (raw[index] & 0x0f)
  }
  return value
}

function decimalBcd(value: number): Uint8Array {
  const result = new Uint8Array(2)
  for (let index = 0; index < 2; index += 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = rawBcdValue(raw)
  if (value === null || value === 0 || value === 16665) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= TONE_DCS_R) return { mode: 'dtcs', ctcss: null, dtcs: value - TONE_DCS_R, polarity: 'R' }
  if (value >= TONE_DCS_N) return { mode: 'dtcs', ctcss: null, dtcs: value - TONE_DCS_N, polarity: 'N' }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): Uint8Array {
  if (channel.toneMode === 'none') return Uint8Array.of(0xff, 0xff)
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    return decimalBcd(Math.round(channel.ctcss * 10))
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error('That DCS code is not supported by this radio.')
    }
    const result = decimalBcd(channel.dtcs)
    result[1] |= channel.dtcsPolarity === 'R' ? 0xc0 : 0x80
    return result
  }
  return Uint8Array.of(0xff, 0xff)
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  return tone.mode === 'dtcs' && tone.dtcs === channel.dtcs && tone.polarity === channel.dtcsPolarity
}

export function parseR2Channel(image: Uint8Array, profile: R2Profile, number: number): WebChannel {
  const offset = 0x10 + (number - 1) * 16
  const raw = image.slice(offset, offset + 16)
  if (raw.byteLength !== 16 || raw[0] === 0xff) return emptyChannel(number)
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency = raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[12] & 0x20 ? 'NFM' : 'FM',
    power: raw[12] & 0x10 ? 'High' : 'Low',
    skip: (raw[12] & 0x08) === 0,
  }
}

export function parseR2Channels(image: Uint8Array, profile: R2Profile): WebChannel[] {
  return Array.from({ length: profile.channelMaximum }, (_, index) => parseR2Channel(image, profile, index + 1))
}

export function applyR2Channel(image: Uint8Array, profile: R2Profile, channel: WebChannel): void {
  if (channel.number < 1 || channel.number > profile.channelMaximum) {
    throw new Error(`Channel must be between 1 and ${profile.channelMaximum}.`)
  }
  const offset = 0x10 + (channel.number - 1) * 16
  const raw = image.subarray(offset, offset + 16)
  if (channel.empty) {
    const reserved = raw.slice(13, 16)
    raw.fill(0xff)
    raw.set(reserved, 13)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const reserved = raw.slice(13, 16)
  raw.fill(0xff)
  raw.set(reserved, 13)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  else raw.subarray(4, 8).fill(0xff)
  const existingTx = decodeTone(raw.slice(10, 12))
  const existingRx = decodeTone(raw.slice(8, 10))
  if (!toneMatches(channel, existingTx) || (!toneMatches(channel, existingRx) && existingRx.mode !== 'none')) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  raw[12] =
    (channel.power === 'High' ? 0x10 : 0) |
    (channel.mode === 'NFM' ? 0x20 : 0) |
    (channel.skip ? 0 : 0x08)
}

async function readAck(transport: RadioTransport): Promise<void> {
  if ((await transport.readExact(1, 2500))[0] !== ACK) throw new Error('The radio rejected the R2 operation.')
}

async function enter(profile: R2Profile, transport: RadioTransport): Promise<void> {
  await transport.discardInput()
  await transport.write(Uint8Array.of(STX))
  let entered = false
  for (let attempt = 0; attempt < 5 && !entered; attempt += 1) {
    await transport.write(new TextEncoder().encode('TYOGRAM'))
    try {
      await readAck(transport)
      entered = true
    } catch {
      // The compact R2 handshake requires several attempts on some firmware.
    }
  }
  if (!entered) throw new Error(`The ${profile.model} radio refused programming mode.`)
  await transport.write(Uint8Array.of(STX))
  const identity = await transport.readExact(8, 2500)
  if (identity[0] !== ACK && identity[0] !== 0x53) throw new Error('Invalid R2 identification.')
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport)
  await transport.write(Uint8Array.of(ENQ))
  const password = await transport.readExact(6, 2500)
  if (!password.every((value) => value === 0xff)) throw new Error('This radio is password protected.')
  await transport.write(Uint8Array.of(ACK))
  try {
    await readAck(transport)
  } catch {
    // Newer RT24/H777S firmware intentionally omits this ACK.
  }
}

export async function readR2RadioWithTransport(
  profile: R2Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      await transport.write(Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE))
      const response = await transport.readExact(BLOCK_SIZE + 4, 2500)
      image.set(response.slice(4), address)
      await transport.write(Uint8Array.of(ACK))
      await readAck(transport)
      onProgress?.({ current: index + 1, total, label: 'Reading Radioddity R2 memory' })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(Uint8Array.of(ACK))
  }
}

export async function writeR2RadioWithTransport(
  profile: R2Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) throw new Error('The selected image is smaller than R2 memory.')
  await enter(profile, transport)
  try {
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      const frame = new Uint8Array(4 + BLOCK_SIZE)
      frame.set([0x57, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE])
      frame.set(image.slice(address, address + BLOCK_SIZE), 4)
      await transport.write(frame)
      await readAck(transport)
      onProgress?.({ current: index + 1, total, label: 'Writing Radioddity R2 memory' })
    }
  } finally {
    await transport.write(Uint8Array.of(ACK))
  }
}
